import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if user has dismissed before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      
      // Show again after 7 days
      if (!dismissed || daysSinceDismissed > 7) {
        setTimeout(() => setShowPrompt(true), 3000); // Show after 3 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS prompt if not installed and on iOS
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      
      if (!dismissed || daysSinceDismissed > 7) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA installed');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-white rounded-2xl shadow-2xl border border-sand overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-forest to-forest/90 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <img 
                src="/icons/icon-96x96.png" 
                alt="Sister Diamonds" 
                className="w-10 h-10 rounded-lg"
              />
            </div>
            <div className="text-white">
              <h3 className="font-heading font-semibold">Sister Diamonds</h3>
              <p className="text-xs text-white/80">Add to Home Screen</p>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-white/80" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {isIOS ? (
            // iOS Instructions
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Install Sister Diamonds on your iPhone for the best experience:
              </p>
              <ol className="text-sm text-gray-600 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-forest/10 text-forest text-xs flex items-center justify-center font-medium">1</span>
                  Tap the Share button <span className="text-lg">↑</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-forest/10 text-forest text-xs flex items-center justify-center font-medium">2</span>
                  Scroll and tap "Add to Home Screen"
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-forest/10 text-forest text-xs flex items-center justify-center font-medium">3</span>
                  Tap "Add" to confirm
                </li>
              </ol>
              <Button 
                onClick={handleDismiss}
                variant="outline"
                className="w-full mt-2"
              >
                Got it
              </Button>
            </div>
          ) : (
            // Chrome/Android Install
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Get quick access to Sister Diamonds from your home screen. No app store needed!
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Smartphone className="h-4 w-4" />
                <span>Works offline • Fast loading • No storage space</span>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleDismiss}
                  variant="outline"
                  className="flex-1"
                >
                  Not now
                </Button>
                <Button 
                  onClick={handleInstall}
                  className="flex-1 bg-forest hover:bg-forest/90 gap-2"
                >
                  <Download className="h-4 w-4" />
                  Install
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
